import { IcDataInfoDocument } from '@assets/icons/24x24';
import { For, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { useNotificationSearchContext } from '@components/cockpit/panels/notificationSearch/NotificationSearchContext';
import { useExportNotificationsMutation } from '@services/data/notification.api';
import { createDownloadToast, exportFileOrDirectory } from '@utils/importExport.utils';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DownloadToastTypeEnum } from '@/types/download.types';

export enum FileFormatEnum {
  CSV = 'CSV',
  KML = 'KML',
  PDF = 'PDF',
}

export function NotificationSearchExportDialog() {
  const { formatMessage } = useIntl();
  const [triggerExportNotifications, { isLoading }] = useExportNotificationsMutation();

  const { criteria, isOpenedExportDialog, setIsOpenedExportDialog } = useNotificationSearchContext();
  const [exportedFormats, setExportedFormats] = useState<Set<FileFormatEnum>>(new Set());

  const handleToggleFileFormat = (fileFormat: FileFormatEnum) => {
    const newSet = new Set(exportedFormats);
    if (exportedFormats.has(fileFormat)) {
      newSet.delete(fileFormat);
    } else {
      newSet.add(fileFormat);
    }
    setExportedFormats(newSet);
  };

  const onClose = () => {
    setIsOpenedExportDialog(false);
    setExportedFormats(new Set());
  };

  const downloadZip = async () => {
    if (exportedFormats.size === 0) {
      return;
    }
    try {
      const response = await triggerExportNotifications({
        exportedFormats: Array.from(exportedFormats),
        criteria: { ...criteria, unpaginated: true },
      });
      exportFileOrDirectory(response);
      onClose();
    } catch (_) {
      createDownloadToast(DownloadToastTypeEnum.ERROR);
    }
  };

  return (
    <CockpitDialog
      open={isOpenedExportDialog}
      onConfirm={downloadZip}
      headerLabel={formatMessage({ id: 'global.export' })}
      isLoading={isLoading}
      confirmLabel={formatMessage({ id: 'global.validate' })}
      isDisabled={exportedFormats.size === 0}
      showContentInfo={false}
      showCancelButton={false}
      size="sm"
      onCancel={onClose}
    >
      <VStack gap={3} width="100%">
        <Text>
          <FormattedMessage id="components.notificationSearch.export.description" />
        </Text>
        <VStack gap={1} width="100%" alignItems="start">
          <For each={Object.values(FileFormatEnum)}>
            {(format) => {
              const isActive = exportedFormats.has(format);
              return (
                <HStack height="48px" gap={2.5}>
                  <IconButton
                    variant="toggle"
                    width="86px"
                    height="100%"
                    data-active={isActive}
                    onClick={() => handleToggleFileFormat(format)}
                  >
                    <HStack gap={1.5}>
                      <Icon as={IcDataInfoDocument} boxSize="24px" color={isActive ? 'neutral.white' : 'neutral.300'} />
                      <FormattedMessage id={`components.notificationSearch.export.fileFormat.${format}`} />
                    </HStack>
                  </IconButton>
                  <Text color="neutral.300">
                    <FormattedMessage id={`components.notificationSearch.export.fileFormatDescription.${format}`} />
                  </Text>
                </HStack>
              );
            }}
          </For>
        </VStack>
      </VStack>
    </CockpitDialog>
  );
}
